import { useQueries } from '@tanstack/react-query'
import { useChains, useConfig } from 'wagmi'

import { RewardData } from './fetchSparkRewards'
import { SimplifiedQueryResult } from '@/domain/common/query'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { vpnCheckQueryOptions } from '@/features/compliance/logic/vpnCheckQueryOptions'
import { claimableRewardsQueryOptions } from '@/features/spark-rewards/data/claimableRewardsQueryOptions'

export type ClaimableRewardDataWithRestrictedCountry = RewardData & {
  isGeoBlocked: boolean
}

export type UseClaimableRewardsQueryResult = SimplifiedQueryResult<ClaimableRewardDataWithRestrictedCountry[]>

export function useClaimableRewardsQuery(): UseClaimableRewardsQueryResult {
  const wagmiConfig = useConfig()
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const { isInSandbox, sandboxChainId } = useSandbox()
  const configuredChains = useChains()

  return useQueries({
    queries: [
      claimableRewardsQueryOptions({
        wagmiConfig,
        account,
        isInSandbox,
        sandboxChainId,
        configuredChains,
      }),
      vpnCheckQueryOptions(),
    ],
    combine: ([rewards, vpnCheck]) => {
      if (rewards.isPending || vpnCheck.isPending) {
        return { isPending: true, isError: false, error: null, data: undefined }
      }

      if (rewards.isError) {
        return { isPending: false, isError: true, error: rewards.error }
      }

      const data = rewards.data.map((reward) => ({
        ...reward,
        isGeoBlocked: reward.restrictedCountryCodes.some((code) => code === vpnCheck.data?.countryCode),
      }))

      return { isPending: false, isError: false, error: null, data }
    },
  })
}
